import { useReactFlow, NodeProps } from '@xyflow/react'
import {
  Save,
  Pencil,
  File,
  FileText,
  CheckCircle,
  AlertCircle,
  Info,
} from 'lucide-react'
import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useUser } from '@/context/UserContext'
import useDataSettings from '@/hooks/useDataSettings'
import {
  useCreateDocument,
  Document,
  DocumentVersion,
} from '@/stores/documentLibraryStore'

// Import AI command utilities
import {
  AICommand,
  AICommandType,
  AICommandStatus,
  createAICommand,
  isCommandComplete,
  isCommandError,
  createNodeCommandPayload,
  useAICommandState,
} from '@/utils/aiCommandUtils'
import NodeBaseActions from '../base/base-actions/NodeBaseActions'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'

interface NodeDocumentDesignerData {
  label?: string
  content?: string
  designParameters?: {
    documentStyle?: string
    customStyle?: string
    audience?: string
    purpose?: string
    technicalLevel?: string
    structure?: string
  }
  templateMarkdown?: string
  generatedMarkdown?: string
  recommendations?: string[]
  statusMessage?: string
  isMinimized?: boolean
  isLocked?: boolean
  documentRequest?: AICommand & {
    isProcessing?: boolean
    message?: string
  }
  assignedEmails?: string[] | string
  // Add dataSettings for type safety
  dataSettings?: any
}

// Define document style options
const documentStyleOptions = [
  { label: 'Technical', value: 'technical' },
  { label: 'Business', value: 'business' },
  { label: 'Educational', value: 'educational' },
  { label: 'Marketing', value: 'marketing' },
  { label: 'Legal', value: 'legal' },
  { label: 'Informal', value: 'informal' },
  { label: 'Formal', value: 'formal' },
  { label: 'Academic', value: 'academic' },
  { label: 'Custom', value: 'custom' },
]

// Define technical level options
const technicalLevelOptions = [
  { label: 'Beginner', value: 'beginner' },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced', value: 'advanced' },
  { label: 'Expert', value: 'expert' },
]

// Add handleScroll utility (copy from DocumentTabs)
const handleScroll = (element: HTMLElement | null, deltaY: number) => {
  if (!element) return false
  const { scrollTop, scrollHeight, clientHeight } = element
  const isAtTop = scrollTop <= 0
  const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2
  const isScrollingUp = deltaY < 0
  const isScrollingDown = deltaY > 0

  if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) {
    element.scrollTop += deltaY
    return true // Event was handled
  }
  return false // Let it bubble for panning
}

const NodeDocumentDesigner: React.FC<
  NodeProps & { data: NodeDocumentDesignerData }
> = (props) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()
  const user = useUser()
  const createDocument = useCreateDocument()

  // ✅ MOVE syncNodeData definition BEFORE any useEffect that uses it
  const syncNodeData = (newData: Partial<NodeDocumentDesignerData>) => {
    const updatedData = { ...data, ...newData }
    updateNodeData(id, updatedData)
  }

  // Use AI command state to manage processing state
  const { isProcessing: aiProcessing, statusMessage: aiStatusMessage } =
    useAICommandState(data.documentRequest, AICommandType.DOCUMENT_DESIGNER)

  // Initialize state from props.data
  const [activeTab, setActiveTab] = useState(0)
  const [content, setContent] = useState(data.content || '')
  const [templateMarkdown, setTemplateMarkdown] = useState(
    data.templateMarkdown || ''
  )
  const [generatedMarkdown, setGeneratedMarkdown] = useState(
    data.generatedMarkdown || ''
  )
  const [designParameters, setDesignParameters] = useState(
    data.designParameters || {
      documentStyle: 'technical',
      customStyle: '',
      audience: '',
      purpose: '',
      technicalLevel: 'intermediate',
      structure: '',
    }
  )
  const [recommendations, setRecommendations] = useState(
    data.recommendations || []
  )
  const [statusMessage, setStatusMessage] = useState(data.statusMessage || '')
  const [isLocked, setIsLocked] = useState(data.isLocked || false)
  const [isMinimized, setIsMinimized] = useState(data.isMinimized || false)
  // ✅ ADD: minimizeLevel state
  const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>(data.minimizeLevel || 'normal')
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editingTitleValue, setEditingTitleValue] = useState(
    data.label || 'Document Designer'
  )
  const [saveDialogVisible, setSaveDialogVisible] = useState(false)
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
    Array.isArray(data.assignedEmails) ? data.assignedEmails : 
    (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : [])
  )
  const [documentForm, setDocumentForm] = useState({
    documentKey: '',
    title: '',
    category: 'Documentation',
    purpose: '',
    userRoles: [],
    producers: [],
    tags: [],
    isKnowledgeSet: false,
    isPublic: false,
  })

  // Add validation state for list fields
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Add local state for purpose input
  const [purposeInput, setPurposeInput] = useState('');

  // Add local state for editable list fields
  const [tagsInput, setTagsInput] = useState('');
  const [userRolesInput, setUserRolesInput] = useState('');
  const [producersInput, setProducersInput] = useState('');

  // When opening the dialog, initialize purposeInput from documentForm (which is filled from config JSON)
  useEffect(() => {
    if (saveDialogVisible) {
      setPurposeInput(documentForm.purpose || '');
    }
  }, [saveDialogVisible, documentForm.purpose]);

  // When opening the dialog, initialize local string states from documentForm
  useEffect(() => {
    if (saveDialogVisible) {
      setTagsInput(Array.isArray(documentForm.tags) ? documentForm.tags.join(', ') : '');
      setUserRolesInput(Array.isArray(documentForm.userRoles) ? documentForm.userRoles.join(', ') : '');
      setProducersInput(Array.isArray(documentForm.producers) ? documentForm.producers.join(', ') : '');
      setPurposeInput(documentForm.purpose || '');
    }
  }, [saveDialogVisible, documentForm]);

  // In the dialog, add local state for extraContext input
  const [extraContextInput, setExtraContextInput] = useState('');
  useEffect(() => {
    if (saveDialogVisible) {
      setExtraContextInput(documentForm.extraContext || '');
    }
  }, [saveDialogVisible, documentForm.extraContext]);

  const titleInputRef = useRef<HTMLInputElement>(null)
  const generatedDocScrollContainerRef = useRef<HTMLDivElement | null>(null)

  // Store the current scroll container for cleanup
  const setGeneratedDocScrollRef = useCallback((el: HTMLDivElement | null) => {
    // Remove listener from previous element
    if (generatedDocScrollContainerRef.current) {
      generatedDocScrollContainerRef.current.removeEventListener('wheel', generatedDocScrollContainerRef.current.__wheelHandler as EventListener);
      // @ts-ignore
      delete generatedDocScrollContainerRef.current.__wheelHandler;
    }
    if (el) {
      // Create a stable handler for this element
      const wheelHandler = (e: WheelEvent) => {
        if (handleScroll(el, e.deltaY)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      // Attach and store handler for cleanup
      el.addEventListener('wheel', wheelHandler, { passive: false });
      // @ts-ignore
      el.__wheelHandler = wheelHandler;
    }
    generatedDocScrollContainerRef.current = el;
  }, []);

  // --- Data Settings state ---
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)

  // --- Data Settings logic (outgoing only) ---
  // Only expose outgoing data (no inputLinks)
  const outgoingFields = {
    content: {
      value: content,
      enabled: true,
      label: 'Document Content',
      description: 'The input content for document design',
      permissions: { readable: true, writable: false },
    },
    templateMarkdown: {
      value: templateMarkdown,
      enabled: !!templateMarkdown,
      label: 'Template Markdown',
      description: 'Generated template markdown',
      permissions: { readable: true, writable: false },
    },
    generatedMarkdown: {
      value: generatedMarkdown,
      enabled: !!generatedMarkdown,
      label: 'Generated Document',
      description: 'The generated document markdown',
      permissions: { readable: true, writable: false },
    },
    recommendations: {
      value: JSON.stringify(recommendations),
      enabled: recommendations.length > 0,
      label: 'Recommendations',
      description: 'AI recommendations for the document',
      permissions: { readable: true, writable: false },
    },
    designParameters: {
      value: JSON.stringify(designParameters),
      enabled: true,
      label: 'Design Parameters',
      description: 'Document design parameters and settings',
      permissions: { readable: true, writable: false },
    },
  }

  // Use useDataSettings for outgoing/exposed data only
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleOpenDataSettingsDialog,
    prepareExposedDataFields,
  } = useDataSettings(
    id,
    data,
    (newData) => updateNodeData(id, { ...data, ...newData }),
    false, // showDebugInfo
    '', // context not relevant here
    new Map(), // sections not relevant
    '', // contentMarkdown not relevant
    setIsDataSettingsDialogVisible
  )

  // Always keep outgoing/exposedData up to date
  useEffect(() => {
    // Only outgoing/exposedData, no inputLinks
    const newDataSettings = {
      ...dataSettings,
      inputLinks: [],
      exposedData: outgoingFields,
      availableNodes: [],
      outputLinks: dataSettings?.outputLinks || [],
      autoSync: false,
    }
    if (JSON.stringify(dataSettings?.exposedData) !== JSON.stringify(outgoingFields)) {
      setDataSettings(newDataSettings)
      syncNodeData({ dataSettings: newDataSettings })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    content,
    templateMarkdown,
    generatedMarkdown,
    recommendations,
    designParameters,
  ])

  // Sync local state when props.data changes
  useEffect(() => {
    if (data.content !== undefined) setContent(data.content)
    if (data.templateMarkdown !== undefined)
      setTemplateMarkdown(data.templateMarkdown)
    if (data.generatedMarkdown !== undefined)
      setGeneratedMarkdown(data.generatedMarkdown)
    if (data.designParameters !== undefined)
      setDesignParameters(data.designParameters)
    if (data.recommendations !== undefined)
      setRecommendations(data.recommendations)
    if (data.statusMessage !== undefined) setStatusMessage(data.statusMessage)
    if (data.isMinimized !== undefined) setIsMinimized(data.isMinimized)
    if (data.isLocked !== undefined) setIsLocked(data.isLocked)
    if (data.label !== undefined) setEditingTitleValue(data.label)
    if (data.assignedEmails !== undefined) 
      setAssignedEmails(Array.isArray(data.assignedEmails) ? data.assignedEmails : 
        (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : []))
  }, [])

  // ✅ ADD: Initialize minimizeLevel if not set
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' });
    } else {
      setMinimizeLevel(data.minimizeLevel);
      setIsMinimized(data.minimizeLevel !== 'normal');
    }
  }, [data.minimizeLevel, id, data, updateNodeData]);

  // ✅ ADD: Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {};
    
    if (content && content.trim()) {
      const wordCount = content.trim().split(/\s+/).length;
      minifiedInfo.content = `${wordCount} words`;
    }
    
    if (designParameters.documentStyle) {
      minifiedInfo.style = designParameters.documentStyle;
    }
    
    if (designParameters.audience?.trim()) {
      minifiedInfo.audience = designParameters.audience;
    }
    
    if (designParameters.technicalLevel) {
      minifiedInfo.level = designParameters.technicalLevel;
    }
    
    if (generatedMarkdown) {
      minifiedInfo.generated = 'Yes';
    }
    
    if (recommendations && recommendations.length > 0) {
      minifiedInfo.recommendations = `${recommendations.length} item${recommendations.length !== 1 ? 's' : ''}`;
    }
    
    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {};
    const hasChanges = JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo);
    
    if (hasChanges) {
      syncNodeData({ minifiedInfo });
    }
  }, [content, designParameters, generatedMarkdown, recommendations, data.minifiedInfo, syncNodeData]);



  // Handle lock/unlock
  const handleLock = () => {
    const newState = !isLocked
    setIsLocked(newState)
    syncNodeData({ isLocked: newState })
  }

  // Handle minimize/maximize
  const handleMinimize = () => {
    const newState = !isMinimized
    setIsMinimized(newState)
    syncNodeData({ isMinimized: newState })
  }

  // ✅ ADD: Handle minimize level change
  const handleMinimizeLevelChange = (level: 'normal' | 'medium' | 'minimum') => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    syncNodeData({ 
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    })
  }

  // Handle delete
  const handleDelete = () => {
    if (
      window.confirm(
        'Are you sure you want to delete this document designer node?'
      )
    ) {
      if ((window as any).__flowDeleteNode) {
        ;(window as any).__flowDeleteNode(id)
      }
    }
  }

  // Handle duplicate
  const handleDuplicate = () => {
    if ((window as any).__flowDuplicateNode) {
      ;(window as any).__flowDuplicateNode(id)
    }
  }

  // Function to handle title edit save
  const handleTitleSave = () => {
    syncNodeData({ label: editingTitleValue })
    setIsEditingTitle(false)
  }

  // Function to handle keypresses in title input
  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave()
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false)
      setEditingTitleValue(data.label || 'Document Designer')
    }
  }

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  // Handle design parameter changes - only sync on blur
  const handleDesignParameterChange = (param: string, value: string) => {
    const updatedDesignParams = { ...designParameters, [param]: value }
    setDesignParameters(updatedDesignParams)
    // Remove synchronization here - will sync on blur instead
  }
  
  // New handler for design parameter blur event
  const handleDesignParameterBlur = () => {
    syncNodeData({ designParameters: designParameters })
  }
  
  // Handle document form changes
  const handleDocumentFormChange = (field: string, value: any) => {
    setDocumentForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Use isProcessing flag from documentRequest, like other nodes
  const isProcessing = !!data.documentRequest?.processing;

  // Generate document based on content and design parameters
  const handleGenerateDocument = () => {
    if (!content.trim()) {
      setStatusMessage('Error: Please enter some content to design a document.')
      syncNodeData({
        statusMessage: 'Error: Please enter some content to design a document.',
      })
      toast.error('Error', {
        description: 'Please enter some content to design a document.',
      })
      return
    }

    const processingMsg = 'Generating document...'
    setStatusMessage(processingMsg)
    
    // Only set sysagent-cmd, do NOT set processing: true here
    const documentRequest = {
      type: AICommandType.DOCUMENT_DESIGNER,
      status: AICommandStatus.STARTED,
      payload: createNodeCommandPayload(id, {
        content: content,
        designParameters: designParameters,
        document: 'document-designer',
      }),
      'sysagent-cmd': "NodeDocumentDesigner-Generate-Started",
      processing: false, // Explicitly false until backend sets true
      message: processingMsg
    }

    syncNodeData({ 
      documentRequest,
      statusMessage: processingMsg,
    })

    toast.info('Processing', {
      description:
        'Generating document based on your parameters. This may take a moment...',
    })
  }

  // Helper to extract only the markdown code block from generatedMarkdown
  function extractMarkdownBlock(markdown: string): string {
    // Find the first ```markdown or ```md code block
    const match = /```(?:markdown|md)?\n([\s\S]*?)```/i.exec(markdown);
    if (match) {
      return match[1].trim();
    }
    // Fallback: try to extract any code block if markdown block not found
    const anyBlock = /```\w*\n([\s\S]*?)```/.exec(markdown);
    if (anyBlock) {
      return anyBlock[1].trim();
    }
    return '';
  }

  // Handle saving to document library
  const handleSaveToLibrary = () => {
    if (!generatedMarkdown.trim()) {
      toast.error('Error', {
        description:
          'Please generate a document first before saving to the library.',
      })
      return
    }

    // --- Extract configuration JSON from generatedMarkdown ---
    let configBlock = null
    try {
      const codeBlocks = Array.from(
        generatedMarkdown.matchAll(/```json(?:\s+configuration)?\n([\s\S]*?)```/g)
      )
      if (codeBlocks.length > 0) {
        configBlock = codeBlocks[0][1].trim()
      }
    } catch (e) {
      // ignore
    }
    let configObj: any = {}
    if (configBlock) {
      try {
        configObj = JSON.parse(configBlock)
      } catch (e) {
        // ignore parse error
      }
    }

    // Prefill the dialog form with node data and config JSON
    setDocumentForm((prev) => ({
      ...prev,
      documentKey: configObj.documentKey || prev.documentKey || '',
      title: configObj.title || editingTitleValue || prev.title || 'Generated Document',
      category: configObj.category || prev.category || 'Documentation',
      purpose: configObj.purpose || designParameters.purpose || prev.purpose || '',
      userRoles: configObj.userRoles || [designParameters.audience || ''].filter(Boolean),
      producers: configObj.producers || prev.producers || [],
      tags: configObj.tags || [
        designParameters.documentStyle || '',
        designParameters.technicalLevel || '',
      ].filter(Boolean),
      isKnowledgeSet: typeof configObj.isKnowledgeSet === 'boolean' ? configObj.isKnowledgeSet : prev.isKnowledgeSet,
      isPublic: typeof configObj.isPublic === 'boolean' ? configObj.isPublic : prev.isPublic,
      extraContext: configObj.extraContext || prev.extraContext || '',
    }))

    // Also set the purposeInput from configObj.purpose (or fallback)
    setPurposeInput(
      configObj.purpose ||
      designParameters.purpose ||
      documentForm.purpose ||
      ''
    );

    setSaveDialogVisible(true)
  }

  // Submit document to the library
  const submitToDocumentLibrary = () => {
    // Validate required fields
    if (
      !documentForm.documentKey ||
      !documentForm.title ||
      !documentForm.category ||
      !purposeInput
    ) {
      toast.error('Validation Error', {
        description:
          'Please fill all required fields (Document Key, Title, Category, Purpose).',
      })
      return
    }

    // Use only the markdown code block for content
    let contentMarkdown = '';
    // Try to extract markdownBlock as in the generated tab rendering
    const codeBlocks = Array.from(
      generatedMarkdown.matchAll(/```(\w+)(?:\s+(\w+))?\n([\s\S]*?)```/g)
    );
    let markdownBlock = null;
    for (const match of codeBlocks) {
      const lang = match[1];
      // Accept both markdown and md
      if (lang === 'markdown' || lang === 'md') {
        markdownBlock = match[3].trim();
        break;
      }
    }
    contentMarkdown = markdownBlock || extractMarkdownBlock(generatedMarkdown);

    // Use extraContext from dialog
    const extraContext = extraContextInput;

    const documentData = {
      documentKey: documentForm.documentKey,
      title: documentForm.title,
      category: documentForm.category,
      purpose: purposeInput,
      extraContext,
      userRoles: documentForm.userRoles || [],
      producers: documentForm.producers || [],
      currentMarkdown: contentMarkdown,
      tags: documentForm.tags || [],
      isKnowledgeSet: documentForm.isKnowledgeSet,
      isPublic: documentForm.isPublic,
    };

    createDocument.execute({
      data: documentData,
      onSuccess: (response) => {
        toast.success('Success', {
          description: 'Document saved to library successfully!',
        })
        setSaveDialogVisible(false)

        // Update status message
        const successMsg = `Document saved to library: ${documentForm.documentKey}`
        setStatusMessage(successMsg)
        syncNodeData({ statusMessage: successMsg })
      },
      onError: (error) => {
        console.error('Error creating document:', error)
        toast.error('Error', {
          description:
            error.response?.data?.error ||
            'Failed to save document to library.',
        })
      },
    })
  }

  // Effect to handle response from system agent
  useEffect(() => {
    // Only the user who started the command should update the node state
    // Use a flag in documentRequest, e.g. handledBy, or check if already handled
    if (
      isCommandComplete(data.documentRequest, AICommandType.DOCUMENT_DESIGNER) &&
      !data.documentRequest?.handledBy
    ) {
      if (data.documentRequest.generatedMarkdown) {
        setGeneratedMarkdown(data.documentRequest.generatedMarkdown);
        syncNodeData({
          generatedMarkdown: data.documentRequest.generatedMarkdown,
          statusMessage: 'Document generation complete.',
          documentRequest: {
            ...data.documentRequest,
            isProcessing: false,
            message: 'Document generation complete.',
            handledBy: user?.email || true, // Mark as handled by this user
          },
        });
      }

      if (data.documentRequest.recommendations) {
        setRecommendations(data.documentRequest.recommendations);
        syncNodeData({
          recommendations: data.documentRequest.recommendations,
          documentRequest: {
            ...data.documentRequest,
            handledBy: user?.email || true, // Mark as handled by this user
          },
        });
      }
      setStatusMessage('Document generation completed.');
      setActiveTab(1);
    } else if (
      isCommandError(data.documentRequest, AICommandType.DOCUMENT_DESIGNER) &&
      !data.documentRequest?.handledBy
    ) {
      setStatusMessage('Error generating document');
      syncNodeData({
        statusMessage: 'Error generating document',
        documentRequest: {
          ...data.documentRequest,
          isProcessing: false,
          error: 'Failed to generate document',
          message: 'Error generating document',
          handledBy: user?.email || true, // Mark as handled by this user
        },
      });

      toast.error('Error', {
        description: 'Failed to generate document. Please try again.',
      });
    }
  }, [data.documentRequest])

  // Handle title change from NodeBaseContainer
  const handleTitleChange = useCallback((newTitle: string) => {
    setEditingTitleValue(newTitle)
    syncNodeData({ label: newTitle })
  }, []);

  // Helper for validating comma-separated list fields
  const validateListField = (value: string) => {
    // Accept empty string as valid (will be [])
    if (!value.trim()) return '';
    // Split and check for empty items
    const arr = value.split(',').map((v) => v.trim()).filter(Boolean);
    if (arr.some((v) => v.includes('\n'))) return 'No line breaks allowed';
    return '';
  };

  // Helper for safely getting textarea value as string
  const getStringValue = (arr: any) => Array.isArray(arr) ? arr.join(', ') : (arr || '');

  // Compute status for NodeBaseContainer
  const nodeStatus = useMemo(() => {
    if (data.documentRequest?.processing) return "loading";
    if (data.statusMessage?.toLowerCase().includes("error")) return "error";
    if (data.statusMessage?.toLowerCase().includes("complete")) return "success";
    if (data.statusMessage?.toLowerCase().includes("saved")) return "success";
    return "initial";
  }, [data.documentRequest?.processing, data.statusMessage]);

  return (
    <>
      <NodeBaseContainer
        id={id}
        data={data}
        resizable={!isLocked && !isProcessing}
        isProcessing={isProcessing}
        assignedEmails={Array.isArray(assignedEmails) ? assignedEmails : (assignedEmails ? assignedEmails.trim().split(/\s+/).filter(Boolean) : [])}
        onAssignUsersClick={() => {
          const emailsStr = window.prompt('Enter emails separated by space', Array.isArray(assignedEmails) ? assignedEmails.join(' ') : (assignedEmails || ''))
          if (emailsStr !== null) {
            const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
            setAssignedEmails(emailsArr)
            syncNodeData({ assignedEmails: emailsArr })
          }
        }}
        nodeTitle={editingTitleValue || 'Document Designer'}
        isLocked={isLocked}
        isMinimized={isMinimized}
        minimizeLevel={minimizeLevel}
        onLockChange={handleLock}
        onMinimizeChange={handleMinimize}
        onMinimizeLevelChange={handleMinimizeLevelChange}
        onTitleChange={handleTitleChange}
        status={nodeStatus}
        selected={selected}
      >
        <div className="flex flex-col h-full w-full rounded-lg shadow">
          <div className="flex justify-between items-center px-4 py-2">
            <NodeBaseActions
              nodeId={id}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onLock={handleLock}
              onUnlock={isLocked}
              onMinimize={handleMinimize}
              onMaximize={isMinimized}
            />
          </div>

          {/* ✅ FIX: Use minimizeLevel instead of isMinimized */}
          {minimizeLevel === 'normal' && (
            <div className="flex flex-col p-2 nodrag h-full">
              <Tabs defaultValue="design" className="flex flex-col h-full" value={typeof activeTab === 'number' ? (activeTab === 0 ? 'design' : activeTab === 1 ? 'generated' : 'recommendations') : activeTab} onValueChange={setActiveTab}>
                <TabsList>
                  <TabsTrigger value="design">Design</TabsTrigger>
                  <TabsTrigger value="generated">
                    Generated Document
                  </TabsTrigger>
                  <TabsTrigger value="recommendations">
                    Recommendations
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="design" className="flex flex-col h-full">
                  <div className="grid grid-cols-12 gap-2 h-full">
                    {/* Left panel - Document Parameters */}
                    <div className="col-span-4 flex flex-col gap-2 overflow-y-auto h-full pr-2">
                      <h3 className="text-lg font-medium mb-2">
                        Document Parameters
                      </h3>
                      <div className="mb-2">
                        <Label className="block text-sm font-medium mb-1">
                          Document Style
                        </Label>
                        <Select
                          value={designParameters.documentStyle}
                          onValueChange={(value) => {
                            handleDesignParameterChange('documentStyle', value)
                            handleDesignParameterBlur() // Sync immediately for select elements
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a style" />
                          </SelectTrigger>
                          <SelectContent>
                            {documentStyleOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {designParameters.documentStyle === 'custom' && (
                        <div className="mb-2">
                          <Label className="block text-sm font-medium mb-1">
                            Custom Style
                          </Label>
                          <Input
                            value={designParameters.customStyle || ''}
                            onChange={(e) =>
                              handleDesignParameterChange(
                                'customStyle',
                                e.target.value
                              )
                            }
                            onBlur={handleDesignParameterBlur}
                            className="w-full"
                            placeholder="Describe your custom style..."
                            disabled={isLocked}
                          />
                        </div>
                      )}

                      <div className="mb-2">
                        <Label className="block text-sm font-medium mb-1">
                          Target Audience
                        </Label>
                        <Input
                          value={designParameters.audience || ''}
                          onChange={(e) =>
                            handleDesignParameterChange(
                              'audience',
                              e.target.value
                            )
                          }
                          onBlur={handleDesignParameterBlur}
                          className="w-full"
                          placeholder="e.g., Developers, Stakeholders, End Users..."
                          disabled={isLocked}
                        />
                      </div>

                      <div className="mb-2">
                        <Label className="block text-sm font-medium mb-1">
                          Document Purpose
                        </Label>
                        <Textarea
                          value={designParameters.purpose || ''}
                          onChange={(e) =>
                            handleDesignParameterChange(
                              'purpose',
                              e.target.value
                            )
                          }
                          onBlur={handleDesignParameterBlur}
                          className="w-full"
                          rows={3}
                          placeholder="Describe the purpose of this document..."
                          disabled={isLocked}
                        />
                      </div>

                      <div className="mb-2">
                        <Label className="block text-sm font-medium mb-1">
                          Technical Level
                        </Label>
                        <Select
                          value={designParameters.technicalLevel}
                          onValueChange={(value) => {
                            handleDesignParameterChange('technicalLevel', value)
                            handleDesignParameterBlur() // Sync immediately for select elements
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a level" />
                          </SelectTrigger>
                          <SelectContent>
                            {technicalLevelOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="mb-2">
                        <Label className="block text-sm font-medium mb-1">
                          Document Structure
                        </Label>
                        <Textarea
                          value={designParameters.structure || ''}
                          onChange={(e) =>
                            handleDesignParameterChange(
                              'structure',
                              e.target.value
                            )
                          }
                          onBlur={handleDesignParameterBlur}
                          className="w-full"
                          rows={3}
                          placeholder="Outline the structure (e.g., Introduction, Background, Details...)"
                          disabled={isLocked}
                        />
                      </div>

                      <Button
                        onClick={handleGenerateDocument}
                        className="mt-2 flex items-center"
                        disabled={isLocked || !content.trim() || isProcessing}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Generate Document
                      </Button>

                      {statusMessage && (
                        <div className="text-sm mt-2 p-2 rounded">
                          {statusMessage}
                        </div>
                      )}
                    </div>

                    {/* Right panel - Content */}
                    <div className="col-span-8 flex flex-col">
                      <h3 className="text-lg font-medium mb-2">Content</h3>
                      <div
                        className="flex-grow overflow-y-auto"
                        style={{ height: '500px' }}
                      >
                        <Textarea
                          className="w-full p-2 resize-none overflow-y-auto"
                          style={{ maxHeight: '100%' }}
                          value={content}
                          onChange={(e) => {
                            setContent(e.target.value)
                            // Remove immediate sync here
                          }}
                          onBlur={() => syncNodeData({ content: content })}
                          placeholder="Enter the content for your document here..."
                          disabled={isLocked}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="generated" className="flex flex-col h-full">
                  {generatedMarkdown ? (
                    <div className="flex flex-col h-full">
                      {/* Scrollable content area with adjusted height */}
                      <div
                        ref={setGeneratedDocScrollRef}
                        className="overflow-y-auto border rounded m-2"
                        style={{ height: 'calc(100% - 120px)' }}
                      >
                        {/* --- CHANGED: Extract and display only JSON config and markdown --- */}
                        {(() => {
                          // Extract code blocks from generatedMarkdown
                          //console.log("generatedMarkdown:", generatedMarkdown)
                          const codeBlocks = Array.from(
                            generatedMarkdown.matchAll(/```(\w+)(?:\s+(\w+))?\n([\s\S]*?)```/g)
                          );
                          let configBlock = null;
                          let markdownBlock = null;
                          for (const match of codeBlocks) {
                            const lang = match[1];
                            const name = match[2];
                            const content = match[3];
                            if ((lang === 'json' && name === 'configuration') || (lang === 'json' && !configBlock)) {
                              configBlock = content.trim();
                              //console.log("configBlock: ", configBlock)
                            }
                            if (lang === 'markdown' || (lang === 'md' && !markdownBlock)) {
                              markdownBlock = content.trim();
                              //console.log("markdownBlock: ", markdownBlock)
                            }
                          }
                          return (
                            <div className="space-y-6">
                              {configBlock && (
                                <div>
                                  <div className="font-semibold mb-1">Configuration (JSON):</div>
                                  <pre className="whitespace-pre-wrap text-left select-text cursor-text p-2 bg-gray-100 rounded text-xs">
                                    {configBlock}
                                  </pre>
                                </div>
                              )}
                              {markdownBlock && (
                                <div>
                                  <div className="font-semibold mb-1">Generated Markdown:</div>
                                  <pre className="whitespace-pre-wrap text-left select-text cursor-text p-2 bg-gray-50 rounded">
                                    {markdownBlock}
                                  </pre>
                                </div>
                              )}
                              {/* If neither block found, fallback to raw output */}
                              {!configBlock && !markdownBlock && (
                                <pre className="whitespace-pre-wrap text-left select-text cursor-text p-4 min-h-0 h-full generated-markdown">
                                  {generatedMarkdown}
                                </pre>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      {/* Button area */}
                      <div
                        className="flex-shrink-0 p-2 bg-white border-t"
                        style={{ minHeight: '130px' }}
                      >
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => setActiveTab(0)}
                            className="flex items-center"
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit Design
                          </Button>
                          <Button
                            onClick={handleSaveToLibrary}
                            className="flex items-center"
                          >
                            <Save className="h-4 w-4 mr-2" />
                            Save to Library
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <p className="text-gray-500 mb-4">
                          No document has been generated yet.
                        </p>
                        <Button
                          onClick={() => setActiveTab(0)}
                          className="flex items-center mx-auto"
                        >
                          <File className="h-4 w-4 mr-2" />
                          Design Document
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent
                  value="recommendations"
                  className="flex flex-col h-full"
                >
                  <div className="flex-1 overflow-y-auto p-2">
                    {recommendations && recommendations.length > 0 ? (
                      <div className="space-y-3">
                        <h3 className="font-semibold text-lg">
                          Document Improvement Suggestions:
                        </h3>
                        <ul className="list-disc pl-5 space-y-2">
                          {recommendations.map((recommendation, index) => (
                            <li key={index} className="text-gray-700">
                              {recommendation}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-gray-500">
                          No recommendations available yet. Generate a document
                          to receive suggestions.
                        </p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </NodeBaseContainer>

      {/* Save to Document Library Dialog */}
      <Dialog open={saveDialogVisible} onOpenChange={setSaveDialogVisible}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save to Document Library</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-6">
              <Label className="block text-sm font-medium mb-1">
                Document Key*
              </Label>
              <Input
                value={documentForm.documentKey}
                onChange={(e) =>
                  handleDocumentFormChange('documentKey', e.target.value)
                }
                className="w-full"
                placeholder="Unique identifier (e.g., DOC-DESIGN-001)"
                required
              />
              <small>Unique identifier for the document</small>
            </div>

            <div className="col-span-6">
              <Label className="block text-sm font-medium mb-1">Title*</Label>
              <Input
                value={documentForm.title}
                onChange={(e) =>
                  handleDocumentFormChange('title', e.target.value)
                }
                className="w-full"
                placeholder="Document title"
                required
              />
            </div>

            <div className="col-span-6">
              <Label className="block text-sm font-medium mb-1">
                Category*
              </Label>
              <Input
                value={documentForm.category}
                onChange={(e) =>
                  handleDocumentFormChange('category', e.target.value)
                }
                className="w-full"
                placeholder="Document category"
                required
              />
            </div>

            <div className="col-span-6">
              <Label className="block text-sm font-medium mb-1">Tags</Label>
              <Textarea
                value={tagsInput}
                onChange={(e) => {
                  setTagsInput(e.target.value);
                  setFormErrors((prev) => ({
                    ...prev,
                    tags: validateListField(e.target.value),
                  }));
                }}
                onBlur={() => {
                  handleDocumentFormChange(
                    'tags',
                    tagsInput
                      .split(',')
                      .map((t) => t.trim())
                      .filter(Boolean)
                  );
                }}
                className="w-full"
                placeholder="Comma-separated tags"
                rows={2}
                autoComplete="off"
              />
              {formErrors.tags && (
                <div className="text-xs text-red-500">{formErrors.tags}</div>
              )}
            </div>

            <div className="col-span-6">
              <Label className="block text-sm font-medium mb-1">
                User Roles
              </Label>
              <Textarea
                value={userRolesInput}
                onChange={(e) => {
                  setUserRolesInput(e.target.value);
                  setFormErrors((prev) => ({
                    ...prev,
                    userRoles: validateListField(e.target.value),
                  }));
                }}
                onBlur={() => {
                  handleDocumentFormChange(
                    'userRoles',
                    userRolesInput
                      .split(',')
                      .map((r) => r.trim())
                      .filter(Boolean)
                  );
                }}
                className="w-full"
                placeholder="Comma-separated roles"
                rows={2}
                autoComplete="off"
              />
              {formErrors.userRoles && (
                <div className="text-xs text-red-500">{formErrors.userRoles}</div>
              )}
              <small>Who needs this kind of material</small>
            </div>

            <div className="col-span-6">
              <Label className="block text-sm font-medium mb-1">
                Producers
              </Label>
              <Textarea
                value={producersInput}
                onChange={(e) => {
                  setProducersInput(e.target.value);
                  setFormErrors((prev) => ({
                    ...prev,
                    producers: validateListField(e.target.value),
                  }));
                }}
                onBlur={() => {
                  handleDocumentFormChange(
                    'producers',
                    producersInput
                      .split(',')
                      .map((p) => p.trim())
                      .filter(Boolean)
                  );
                }}
                className="w-full"
                placeholder="Comma-separated producers"
                rows={2}
                autoComplete="off"
              />
              {formErrors.producers && (
                <div className="text-xs text-red-500">{formErrors.producers}</div>
              )}
              <small>Who produces this kind of material</small>
            </div>

            <div className="col-span-6">
              <div className="flex items-center mt-4">
                <Checkbox
                  id="isKnowledgeSet"
                  checked={documentForm.isKnowledgeSet}
                  onCheckedChange={(checked) =>
                    handleDocumentFormChange('isKnowledgeSet', checked)
                  }
                />
                <Label htmlFor="isKnowledgeSet" className="ml-2">
                  Include in Knowledge Set
                </Label>
              </div>
              <small className="block mt-1">
                Make this document available for AI knowledge retrieval
              </small>
            </div>

            <div className="col-span-6">
              <div className="flex items-center mt-4">
                <Checkbox
                  id="isPublic"
                  checked={documentForm.isPublic}
                  onCheckedChange={(checked) =>
                    handleDocumentFormChange('isPublic', checked)
                  }
                />
                <Label htmlFor="isPublic" className="ml-2">
                  Make Public
                </Label>
              </div>
              <small className="block mt-1">
                Allow public access to this document
              </small>
            </div>

            <div className="col-span-12 mt-4">
              <p className="text-sm text-gray-500">
                The document design parameters will be stored in the extra
                context field for future reference.
              </p>
            </div>

            <div className="col-span-12">
              <Label className="block text-sm font-medium mb-1">Extra Context</Label>
              <Textarea
                value={extraContextInput}
                onChange={(e) => setExtraContextInput(e.target.value)}
                onBlur={() => handleDocumentFormChange('extraContext', extraContextInput)}
                className="w-full"
                rows={3}
                placeholder="Any extra context as JSON or text"
              />
              <small>Optional: Additional context or metadata for this document</small>
            </div>

            <div className="col-span-12">
              <Label className="block text-sm font-medium mb-1">Purpose</Label>
              <Textarea
                value={purposeInput}
                onChange={(e) => setPurposeInput(e.target.value)}
                onBlur={() => handleDocumentFormChange('purpose', purposeInput)}
                className="w-full"
                rows={3}
                placeholder="Document purpose"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setSaveDialogVisible(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleDocumentFormChange(
                  'tags',
                  tagsInput.split(',').map((t) => t.trim()).filter(Boolean)
                );
                handleDocumentFormChange(
                  'userRoles',
                  userRolesInput.split(',').map((r) => r.trim()).filter(Boolean)
                );
                handleDocumentFormChange(
                  'producers',
                  producersInput.split(',').map((p) => p.trim()).filter(Boolean)
                );
                handleDocumentFormChange('purpose', purposeInput);
                handleDocumentFormChange('extraContext', extraContextInput);
                // Validate before submit
                const tagsError = validateListField(tagsInput);
                const userRolesError = validateListField(userRolesInput);
                const producersError = validateListField(producersInput);
                setFormErrors({
                  tags: tagsError,
                  userRoles: userRolesError,
                  producers: producersError,
                });
                if (tagsError || userRolesError || producersError) {
                  toast.error('Validation Error', {
                    description: 'Please fix errors in the list fields.',
                  });
                  return;
                }
                submitToDocumentLibrary();
              }}
              className="flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default memo(NodeDocumentDesigner)
